import { ApiClient } from './apiClient';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://localhost:7141/api/v1';

export interface SystemRuleTypeInfo {
  ruleNo: number;
  name: string;
  description: string;
}

export interface SystemRuleTestRequest {
  ruleNo: number;
  warehouseID: string;
  sqlExtraFilter?: string;
  sqlSorting?: string;
  maxRecords?: number;
  parameters?: Record<string, any>;
}

export interface SystemRuleTestResult {
  ruleNo: number;
  ruleName: string;
  success: boolean;
  message: string;
  recordsAffected: number;
  errors: string[];
  warnings: string[];
  startTime: string;
  endTime: string;
  executionTime: string;
  additionalData: Record<string, any>;
}

export interface RuleGroupParameters {
  ruleGroupID: number;
  companyNo: string;
  warehouseID: string;
  sessionID: string;
  debugMode: boolean;
  sqlExtraFilter?: string;
}

export interface SystemRuleParameters {
  companyNo: string;
  warehouseID: string;
  sqlFilter: string;
  orderLineIDs: string;
  createPutAwayOnIncompleteItems: boolean;
  sqlSorting: string;
  sessionID: string;
  debugMode: boolean;
  maxRecords: number;
  additionalParameters: Record<string, any>;
}

export interface SystemRuleGroup {
  ruleGroupID: number;
  companyNo: string;
  ruleGroupName: string;
  description: string;
  activeYesNo: boolean;
  rules: SystemRule[];
}

export interface SystemRule {
  ruleID: number;
  companyNo: string;
  ruleName: string;
  description: string;
  activeYesNo: boolean;
  execOrder: number;
  ruleType: RuleType;
  sqlFilter: string;
  sqlSorting: string;
  sqlFilterSyntax: string;
  sqlGroupSyntax: string;
  sqlOrderSyntax: string;
  pListBatchTypeNo: number;
  createPutAwayOnIncompleteItems: boolean;
  sqlExpression: string;
  ruleParameters: Record<string, any>;
}

export enum RuleType {
  System = 0,
  User = 1
}

export interface RuleExecutionResult {
  ruleID: number;
  ruleName: string;
  execOrder: number;
  success: boolean;
  message: string;
  recordsAffected: number;
  errors: string[];
  warnings: string[];
  additionalData: Record<string, any>;
  startTime: string;
  endTime: string;
  executionTime: string;
}

export interface RuleGroupResult {
  success: boolean;
  message: string;
  totalRulesExecuted: number;
  totalRulesSucceeded: number;
  totalRulesFailed: number;
  totalRecordsAffected: number;
  errors: string[];
  warnings: string[];
  ruleResults: RuleExecutionResult[];
  additionalData: Record<string, any>;
  startTime: string;
  endTime: string;
  totalExecutionTime: string;
}

export interface RuleGroupValidationResult {
  isValid: boolean;
  ruleGroupName: string;
  ruleCount: number;
  errors: string[];
  warnings: string[];
}

class RuleGroupEngineService {
  private baseUrl: string;

  constructor() {
    this.baseUrl = API_BASE_URL;
  }

  /**
   * Execute a complete rule group
   */
  async executeRuleGroup(companyNo: string, parameters: RuleGroupParameters): Promise<RuleGroupResult> {
    try {
      const response = await ApiClient.post(
        `${this.baseUrl}/${companyNo}/rulegroupengine/execute`,
        parameters
      );
        return response.json();
    } catch (error: any) {
      throw new Error(`Failed to execute rule group: ${error.response?.data?.message || error.message}`);
    }
  }

  /**
   * Get available rule groups for a company
   */
  async getRuleGroups(companyNo: string): Promise<SystemRuleGroup[]> {
    try {
      const response = await ApiClient.get(`${this.baseUrl}/${companyNo}/rulegroupengine/rulegroups`);
      return response.json();
    } catch (error: any) {
      throw new Error(`Failed to get rule groups: ${error.response?.data?.message || error.message}`);
    }
  }

  /**
   * Get a specific rule group with all rules
   */
  async getRuleGroup(companyNo: string, ruleGroupId: number): Promise<SystemRuleGroup> {
    try {
        const response = await ApiClient.get(`${this.baseUrl}/${companyNo}/rulegroupengine/rulegroups/${ruleGroupId}`);
        return response.json();
    } catch (error: any) {
      throw new Error(`Failed to get rule group: ${error.response?.data?.message || error.message}`);
    }
  }

  /**
   * Get all available system rules (the 5 rule types)
   */
  async getSystemRuleTypes(companyNo: string): Promise<SystemRuleTypeInfo[]> {
    try {
        const response = await ApiClient.get(`${this.baseUrl}/${companyNo}/rulegroupengine/systemruletypes`);
        return response.json();
    } catch (error: any) {
      throw new Error(`Failed to get system rule types: ${error.response?.data?.message || error.message}`);
    }
  }

  /**
   * Test a single system rule type
   */
  async testSystemRule(companyNo: string, testRequest: SystemRuleTestRequest): Promise<SystemRuleTestResult> {
    try {
      const response = await ApiClient.post(
        `${this.baseUrl}/${companyNo}/rulegroupengine/test/systemrule`,
        testRequest
      );
        return response.json();
    } catch (error: any) {
      throw new Error(`Failed to test system rule: ${error.response?.data?.message || error.message}`);
    }
  }

  /**
   * Validate rule group parameters before execution
   */
  async validateRuleGroup(companyNo: string, parameters: RuleGroupParameters): Promise<RuleGroupValidationResult> {
    try {
      const response = await ApiClient.post(
        `${this.baseUrl}/${companyNo}/rulegroupengine/validate`,
        parameters
      );
        return response.json();
    } catch (error: any) {
      throw new Error(`Failed to validate rule group: ${error.response?.data?.message || error.message}`);
    }
  }

  /**
   * Create a sample rule group parameters object for testing
   */
  createSampleParameters(companyNo: string, ruleGroupId: number, warehouseId: string = '', sqlExtraFilter?: string): RuleGroupParameters {
    return {
      ruleGroupID: ruleGroupId,
      companyNo: companyNo,
      warehouseID: warehouseId,
      sessionID: `TEST_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      debugMode: true,
      sqlExtraFilter: sqlExtraFilter
    };
  }

  /**
   * Create test parameters for individual system rule testing
   */
  createSystemRuleTestRequest(ruleNo: number, warehouseId: string = ''): SystemRuleTestRequest {
    return {
      ruleNo: ruleNo,
      warehouseID: warehouseId,
      maxRecords: 50,
      parameters: {
        testMode: true,
        debugMode: true
      }
    };
  }

  /**
   * Format execution time for display
   */
  formatExecutionTime(timeSpan: string): string {
    if (!timeSpan) return '0ms';
    
    try {
      const parts = timeSpan.split(':');
      if (parts.length === 3) {
        const hours = parseInt(parts[0]);
        const minutes = parseInt(parts[1]);
        const seconds = parseFloat(parts[2]);
        
        if (hours > 0) {
          return `${hours}h ${minutes}m ${seconds.toFixed(2)}s`;
        } else if (minutes > 0) {
          return `${minutes}m ${seconds.toFixed(2)}s`;
        } else if (seconds >= 1) {
          return `${seconds.toFixed(2)}s`;
        } else {
          return `${(seconds * 1000).toFixed(0)}ms`;
        }
      }
    } catch (error) {
      console.warn('Error formatting execution time:', error);
    }
    
    return timeSpan;
  }

  /**
   * Get color class for rule result status
   */
  getStatusColor(success: boolean, hasWarnings: boolean = false): string {
    if (success) {
      return hasWarnings ? 'text-yellow-600' : 'text-green-600';
    }
    return 'text-red-600';
  }

  /**
   * Get icon for rule result status
   */
  getStatusIcon(success: boolean, hasWarnings: boolean = false): string {
    if (success) {
      return hasWarnings ? '⚠️' : '✅';
    }
    return '❌';
  }
}

const ruleGroupEngineService = new RuleGroupEngineService();
export default ruleGroupEngineService;
